// Formatowanie do tabel panelu. Wszystko po polsku, bo to jest to, co widzi
// człowiek po drugiej stronie ekranu.

/**
 * Kwota z groszy na złotówki. Liczymy na liczbach całkowitych - kwoty w tym
 * projekcie NIGDY nie przechodzą przez zmiennoprzecinkowe dzielenie, bo grosz
 * zgubiony w formatowaniu potrafi wrócić jako rozbieżność w rozliczeniu.
 */
export function formatMoney(grosze: number, currency = "PLN"): string {
  const ujemna = grosze < 0;
  const wartosc = Math.abs(Math.trunc(grosze));
  const zlote = Math.trunc(wartosc / 100);
  const reszta = wartosc % 100;
  const zGrupami = String(zlote).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  return `${ujemna ? "-" : ""}${zGrupami},${String(reszta).padStart(2, "0")} ${currency}`;
}

const DATE_TIME = new Intl.DateTimeFormat("pl-PL", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  timeZone: "Europe/Warsaw",
});

const DATE_ONLY = new Intl.DateTimeFormat("pl-PL", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  timeZone: "Europe/Warsaw",
});

/** Puste pole to "-", nie "Invalid Date" i nie pusty prostokąt. */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : DATE_TIME.format(date);
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : DATE_ONLY.format(date);
}

export type Tone = "ok" | "warn" | "error" | "neutral";

interface StatusInfo {
  label: string;
  tone: Tone;
}

const ORDER_STATUSES: Record<string, StatusInfo> = {
  pending: { label: "Czeka na płatność", tone: "warn" },
  paid: { label: "Opłacone", tone: "ok" },
  failed: { label: "Nieudane", tone: "error" },
  cancelled: { label: "Anulowane", tone: "neutral" },
  refunded: { label: "Zwrócone", tone: "neutral" },
  partially_refunded: { label: "Zwrócone częściowo", tone: "warn" },
};

const PAYMENT_STATUSES: Record<string, StatusInfo> = {
  initiated: { label: "Rozpoczęta", tone: "neutral" },
  pending: { label: "W toku", tone: "warn" },
  succeeded: { label: "Potwierdzona", tone: "ok" },
  failed: { label: "Nieudana", tone: "error" },
  refunded: { label: "Zwrócona", tone: "neutral" },
};

const DELIVERY_STATUSES: Record<string, StatusInfo> = {
  queued: { label: "W kolejce", tone: "neutral" },
  sent: { label: "Wysłane", tone: "ok" },
  delivered: { label: "Doręczone", tone: "ok" },
  opened: { label: "Otwarte", tone: "ok" },
  answered: { label: "Odpowiedziane", tone: "ok" },
  failed: { label: "Błąd wysyłki", tone: "error" },
  skipped: { label: "Pominięte", tone: "neutral" },
};

const SHIPMENT_STATUSES: Record<string, StatusInfo> = {
  created: { label: "Utworzona", tone: "neutral" },
  confirmed: { label: "Potwierdzona", tone: "neutral" },
  label_ready: { label: "Etykieta gotowa", tone: "neutral" },
  in_transit: { label: "W drodze", tone: "warn" },
  ready_for_pickup: { label: "Czeka w punkcie", tone: "warn" },
  delivered: { label: "Doręczona", tone: "ok" },
  returned: { label: "Zwrócona", tone: "warn" },
  error: { label: "Błąd", tone: "error" },
};

const SHIPMENT_KINDS: Record<string, string> = {
  starter_box: "Starter Box",
  book: "Książka",
  copy: "Kopia książki",
};

const MEMOIR_STATUSES: Record<string, StatusInfo> = {
  setup: { label: "W konfiguracji", tone: "neutral" },
  active: { label: "Aktywna", tone: "ok" },
  paused: { label: "Wstrzymana", tone: "warn" },
  book_editing: { label: "Skład książki", tone: "neutral" },
  completed: { label: "Zakończona", tone: "neutral" },
};

const STORY_STATUSES: Record<string, StatusInfo> = {
  draft: { label: "Szkic", tone: "neutral" },
  transcribing: { label: "Przepisywana", tone: "warn" },
  review: { label: "Do przejrzenia", tone: "warn" },
  published: { label: "Opublikowana", tone: "ok" },
  hidden: { label: "Ukryta", tone: "neutral" },
};

const INBOUND_STATUSES: Record<string, StatusInfo> = {
  unmatched: { label: "Do przypisania", tone: "warn" },
  matched: { label: "Rozpoznana", tone: "neutral" },
  assigned: { label: "Przypisana", tone: "ok" },
  rejected: { label: "Odrzucona", tone: "neutral" },
};

const TRANSCRIPT_STATUSES: Record<string, StatusInfo> = {
  pending: { label: "Czeka w kolejce", tone: "warn" },
  processing: { label: "Przepisywanie trwa", tone: "warn" },
  done: { label: "Przepisane", tone: "ok" },
  failed: { label: "Nie udało się", tone: "error" },
};

const RECORDING_STATUSES: Record<string, StatusInfo> = {
  uploading: { label: "Wgrywanie", tone: "warn" },
  ready: { label: "W magazynie", tone: "ok" },
  failed: { label: "Nie dojechało", tone: "error" },
};

/**
 * Statusy rozmowy telefonicznej. Formy bezosobowe ("nie odebrano"), bo
 * narratorem bywa i babcia, i dziadek - ta sama zasada co przy czasownikach
 * na okładce książki.
 *
 * "Bez potwierdzenia" to stan `unknown`: zlecenie poszło, odpowiedź od
 * dostawcy nie wróciła i nie wiadomo, czy telefon zadzwonił. Wygląda jak
 * awaria i nią nie jest - dlatego ma własną etykietę, a nie "Nieudana".
 */
const INTERVIEW_STATUSES: Record<string, StatusInfo> = {
  scheduled: { label: "Zaplanowana", tone: "neutral" },
  dialing: { label: "Dzwonimy", tone: "warn" },
  in_progress: { label: "Rozmowa trwa", tone: "warn" },
  completed: { label: "Odbyta", tone: "ok" },
  no_answer: { label: "Nie odebrano", tone: "warn" },
  busy: { label: "Zajęte", tone: "warn" },
  failed: { label: "Nie udało się zadzwonić", tone: "error" },
  declined: { label: "Odrzucone", tone: "neutral" },
  unknown: { label: "Bez potwierdzenia", tone: "error" },
};

/** Dlaczego rozmowa się nie odbyła. Od tego zależy, czy dzwonimy znowu. */
const FAILURE_KINDS: Record<string, string> = {
  busy: "Zajęte",
  no_answer: "Nie odebrano",
  declined: "Rozmówca odrzucił połączenie",
  invalid_number: "Nieprawidłowy numer",
  provider_error: "Awaria u dostawcy",
  unknown: "Nie wiadomo",
};

const VOICE_PROVIDERS: Record<string, string> = {
  elevenlabs: "ElevenLabs",
  // Bez kluczy rozmowa idzie na konsolę i nikt nigdzie nie dzwoni.
  console: "Konsola",
};

/** Kto mówi w zapisie rozmowy. Do książki idą wyłącznie słowa narratora. */
const INTERVIEW_ROLES: Record<string, string> = {
  agent: "Agent",
  user: "Narrator",
};

/**
 * Wersje tekstu historii. "Oryginał" to zapis tego, co narrator powiedział,
 * "Uporządkowana" - ta sama treść po redakcji. Nazwy muszą być identyczne w
 * każdym widoku, bo różnica między nimi jest tu istotą sprawy.
 */
const STORY_VERSIONS: Record<string, string> = {
  original: "Oryginał",
  cleaned: "Uporządkowana",
  edited: "Poprawiona przez rodzinę",
};

const PLANS: Record<string, string> = {
  digital: "Cyfrowy",
  book: "Książka",
  premium: "Premium",
  family: "Rodzina",
};

const ANSWERED_VIA: Record<string, string> = {
  email: "E-mailem",
  web: "Na stronie",
  sms: "SMS-em",
  voice: "Głosem",
  proxy: "Przez rodzinę",
};

const FAMILY_ROLES: Record<string, string> = {
  owner: "Właściciel",
  caretaker: "Opiekun",
  member: "Członek rodziny",
};

/**
 * Dni tygodnia od razu w formie z przyimkiem. Biernik polskich nazw jest
 * nieregularny ("w środę", "we wtorek"), więc nie składamy go z reguł -
 * ta sama zasada co przy imionach narratorów.
 */
const WEEKDAYS_WITH_PREPOSITION = [
  "w poniedziałek",
  "we wtorek",
  "w środę",
  "w czwartek",
  "w piątek",
  "w sobotę",
  "w niedzielę",
];

/**
 * Kanały wysyłki pytania. "Telefon" to rozmowa z agentem głosowym - w delivery
 * logu wygląda jak każda inna wysyłka, więc bez tej pozycji renderował się
 * surowy "phone" i nie dało się go odfiltrować.
 */
const CHANNELS: Record<string, string> = {
  email: "E-mail",
  sms: "SMS",
  phone: "Telefon",
};

const SENSITIVITY: Record<string, string> = {
  normal: "Zwykłe",
  sensitive: "Delikatne",
};

const PHASES: Record<string, string> = {
  opening: "Otwarcie",
  early: "Początek",
  middle: "Środek",
  late: "Końcówka",
  closing: "Zamknięcie",
  any: "Dowolna",
};

const ROLES: Record<string, string> = {
  superadmin: "Superadmin",
  operations: "Operacje",
  support: "Wsparcie",
};

/** Nieznanej wartości nie udajemy - pokazujemy ją surową i na szaro. */
function lookup(dictionary: Record<string, StatusInfo>, value: string | null): StatusInfo {
  if (!value) return { label: "-", tone: "neutral" };
  return dictionary[value] ?? { label: value, tone: "neutral" };
}

export const orderStatus = (value: string | null): StatusInfo => lookup(ORDER_STATUSES, value);
export const paymentStatus = (value: string | null): StatusInfo => lookup(PAYMENT_STATUSES, value);
export const deliveryStatus = (value: string | null): StatusInfo =>
  lookup(DELIVERY_STATUSES, value);
export const shipmentStatus = (value: string | null): StatusInfo =>
  lookup(SHIPMENT_STATUSES, value);

export const memoirStatus = (value: string | null): StatusInfo => lookup(MEMOIR_STATUSES, value);
export const storyStatus = (value: string | null): StatusInfo => lookup(STORY_STATUSES, value);
export const inboundStatus = (value: string | null): StatusInfo => lookup(INBOUND_STATUSES, value);
export const transcriptStatus = (value: string | null): StatusInfo =>
  lookup(TRANSCRIPT_STATUSES, value);
export const recordingStatus = (value: string | null): StatusInfo =>
  lookup(RECORDING_STATUSES, value);
export const interviewStatus = (value: string | null): StatusInfo =>
  lookup(INTERVIEW_STATUSES, value);

export const failureKindLabel = (value: string | null): string =>
  (value && FAILURE_KINDS[value]) || value || "-";
export const voiceProviderLabel = (value: string | null): string =>
  (value && VOICE_PROVIDERS[value]) || value || "-";
export const interviewRoleLabel = (value: string | null): string =>
  (value && INTERVIEW_ROLES[value]) || value || "-";

/**
 * Czas trwania rozmowy. Same sekundy nic nie mówią przy dłuższej rozmowie
 * ("742 s"), a minuty z ułamkiem kłamią o dokładności, której nie mamy.
 */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return "-";
  const calkowite = Math.max(0, Math.trunc(seconds));
  const minuty = Math.trunc(calkowite / 60);
  const reszta = calkowite % 60;
  return minuty === 0 ? `${reszta} s` : `${minuty} min ${reszta} s`;
}

/** Znacznik czasu w rozmowie: "2:07 od początku". */
export function formatCallOffset(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return "";
  const calkowite = Math.max(0, Math.trunc(seconds));
  return `${Math.trunc(calkowite / 60)}:${String(calkowite % 60).padStart(2, "0")}`;
}

export const shipmentKind = (value: string | null): string =>
  (value && SHIPMENT_KINDS[value]) || value || "-";
export const channelLabel = (value: string | null): string =>
  (value && CHANNELS[value]) || value || "-";
export const planLabel = (value: string | null): string => (value && PLANS[value]) || value || "-";
export const answeredViaLabel = (value: string | null): string =>
  (value && ANSWERED_VIA[value]) || value || "-";
export const familyRoleLabel = (value: string | null): string =>
  (value && FAMILY_ROLES[value]) || value || "-";
export const storyVersionLabel = (value: string | null): string =>
  (value && STORY_VERSIONS[value]) || value || "-";

/** Harmonogram czyta się jako zdanie, nie jako para liczb "6 / 10". */
export function scheduleLabel(weekday: number, hour: number, frequency: string): string {
  const dzien = WEEKDAYS_WITH_PREPOSITION[weekday] ?? `dzień ${weekday}`;
  const co = frequency === "biweekly" ? "Co dwa tygodnie" : "Co tydzień";
  return `${co} ${dzien}, godzina ${String(hour).padStart(2, "0")}:00`;
}
export const sensitivityLabel = (value: string | null): string =>
  (value && SENSITIVITY[value]) || value || "-";
export const phaseLabel = (value: string | null): string => (value && PHASES[value]) || value || "-";
export const roleLabel = (value: string | null): string => (value && ROLES[value]) || value || "-";

export const ORDER_STATUS_OPTIONS = Object.entries(ORDER_STATUSES).map(([value, info]) => ({
  value,
  label: info.label,
}));

export const DELIVERY_STATUS_OPTIONS = Object.entries(DELIVERY_STATUSES).map(([value, info]) => ({
  value,
  label: info.label,
}));

export const SHIPMENT_STATUS_OPTIONS = Object.entries(SHIPMENT_STATUSES).map(([value, info]) => ({
  value,
  label: info.label,
}));

export const SHIPMENT_KIND_OPTIONS = Object.entries(SHIPMENT_KINDS).map(([value, label]) => ({
  value,
  label,
}));

export const MEMOIR_STATUS_OPTIONS = Object.entries(MEMOIR_STATUSES).map(([value, info]) => ({
  value,
  label: info.label,
}));

export const STORY_STATUS_OPTIONS = Object.entries(STORY_STATUSES).map(([value, info]) => ({
  value,
  label: info.label,
}));

export const INBOUND_STATUS_OPTIONS = Object.entries(INBOUND_STATUSES).map(([value, info]) => ({
  value,
  label: info.label,
}));

export const TRANSCRIPT_STATUS_OPTIONS = Object.entries(TRANSCRIPT_STATUSES).map(
  ([value, info]) => ({ value, label: info.label }),
);

export const INTERVIEW_STATUS_OPTIONS = Object.entries(INTERVIEW_STATUSES).map(([value, info]) => ({
  value,
  label: info.label,
}));

export const PLAN_OPTIONS = Object.entries(PLANS).map(([value, label]) => ({ value, label }));

export const CHANNEL_OPTIONS = Object.entries(CHANNELS).map(([value, label]) => ({ value, label }));

export const ANSWERED_VIA_OPTIONS = Object.entries(ANSWERED_VIA).map(([value, label]) => ({
  value,
  label,
}));

/**
 * Odsetek odpowiedzi. Zero wysyłek to nie zero procent, tylko brak danych -
 * mylenie tych dwóch rzeczy jest najkrótszą drogą do złego wniosku.
 */
export function responseRate(sent: number, answered: number): number | null {
  if (sent <= 0) return null;
  return Math.round((answered / sent) * 100);
}

/** Trzy formy mnogiej w polszczyźnie. Ta sama zasada co w apps/web/lib/plural.ts. */
export function plural(count: number, one: string, few: string, many: string): string {
  const abs = Math.abs(count);
  if (abs === 1) return one;
  const lastTwo = abs % 100;
  const last = abs % 10;
  if (last >= 2 && last <= 4 && !(lastTwo >= 12 && lastTwo <= 14)) return few;
  return many;
}
